import React, { useRef } from 'react';
import { Shield } from 'lucide-react';
import { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface UserRolesSectionProps {
  roles: string[];
  onRoleSelect: (role: string) => void;
  loading: boolean;
}

// Administrator og Bruker er gjensidig utelukkende (migrasjon 066): nøyaktig én er valgt.
// Superadmin er en plattformrolle uten organisasjon og kan ikke tildeles her.
const availableRoles = [
  {
    value: 'admin',
    label: 'Administrator',
    activeClass: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    description: 'Full tilgang til produksjoner, bemanning, brukere og innstillinger',
  },
  {
    value: 'user',
    label: 'Bruker',
    activeClass: 'bg-primary text-primary-foreground hover:bg-primary/90',
    description: 'Ser publiserte produksjoner og styrer egen profil og egne talenter',
  },
];

const UserRolesSection: React.FC<UserRolesSectionProps> = ({
  roles,
  onRoleSelect,
  loading,
}) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Gamle data kan ha begge roller; Administrator vinner, som i backend.
  let selected: string | null = null;
  if (roles.includes('admin')) selected = 'admin';
  else if (roles.includes('user')) selected = 'user';

  // Roving tabindex: bare valgt rolle er tabbable; uten valg er den første det.
  const tabbableValue = selected ?? availableRoles[0].value;

  // WAI-ARIA radiogruppe: piltaster velger neste/forrige (med wrap) og flytter fokus.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (index + 1) % availableRoles.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (index - 1 + availableRoles.length) % availableRoles.length;
    } else {
      return;
    }
    event.preventDefault();
    onRoleSelect(availableRoles[next].value);
    buttonRefs.current[next]?.focus();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Shield className="h-5 w-5 text-primary" />
        <h4 id="user-roles-label" className="text-sm font-semibold">Rolle</h4>
      </div>

      <div role="radiogroup" aria-labelledby="user-roles-label" className="flex gap-2 flex-wrap">
        {availableRoles.map((role, index) => {
          const isActive = selected === role.value;
          return (
            <button
              key={role.value}
              ref={(el) => { buttonRefs.current[index] = el; }}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={role.label}
              tabIndex={role.value === tabbableValue ? 0 : -1}
              disabled={loading}
              title={role.description}
              onClick={() => onRoleSelect(role.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={cn(
                badgeVariants({ variant: isActive ? 'default' : 'outline' }),
                loading ? 'cursor-default opacity-60' : 'cursor-pointer',
                isActive && role.activeClass
              )}
            >
              {role.label}
            </button>
          );
        })}
      </div>

      <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
        {availableRoles.map((role) => (
          <li key={role.value}>
            <span className="font-medium">{role.label}:</span> {role.description}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default UserRolesSection;
